import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import './ArtifactVisualizer.css';

const ArtifactVisualizer = ({
  artifactId,
  width = '100%',
  height = '100%',
  onLoad,
  onError
}) => {
  const [phase, setPhase] = useState('loading'); // loading | iframe-loading | ready | error
  const [errorMsg, setErrorMsg] = useState(null);
  const [sandboxHtml, setSandboxHtml] = useState(null);
  const [browserCode, setBrowserCode] = useState(null);
  const iframeRef = useRef(null);
  const onLoadRef = useRef(onLoad);
  const onErrorRef = useRef(onError);

  useEffect(() => { onLoadRef.current = onLoad; }, [onLoad]);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);

  // Fetch sandbox HTML and browser-ready code in parallel
  useEffect(() => {
    if (!artifactId) {
      setErrorMsg('No visualization ID provided');
      setPhase('error');
      return;
    }

    setPhase('loading');
    const controller = new AbortController();
    const fetchTimeout = setTimeout(() => controller.abort(), 30000); // 30s

    Promise.all([
      fetch(`/api/artifacts/${artifactId}/sandbox`, { signal: controller.signal })
        .then(r => {
          if (!r.ok) throw new Error('Failed to load sandbox');
          return r.text();
        }),
      fetch(`/api/artifacts/${artifactId}/browser-ready`, { signal: controller.signal })
        .then(r => {
          if (!r.ok) throw new Error('Failed to load code');
          return r.json();
        })
        .then(data => data.code || data)
    ])
      .then(([html, code]) => {
        setSandboxHtml(html);
        setBrowserCode(code);
        setPhase('iframe-loading');
      })
      .catch(err => {
        if (err.name === 'AbortError') return;
        setErrorMsg(err.message);
        setPhase('error');
        if (onErrorRef.current) onErrorRef.current(err);
      })
      .finally(() => clearTimeout(fetchTimeout));

    return () => {
      clearTimeout(fetchTimeout);
      controller.abort();
    };
  }, [artifactId]);

  // Listen for messages from the sandbox iframe
  useEffect(() => {
    if (phase !== 'iframe-loading') return;

    const handleMessage = (event) => {
      const data = event.data;
      if (!data || !data.type) return;

      if (data.type === 'iframe-ready' && browserCode) {
        // Sandbox is ready — send the code to execute
        const iframe = iframeRef.current;
        if (iframe && iframe.contentWindow) {
          iframe.contentWindow.postMessage({
            type: 'execute-artifact',
            code: browserCode,
            artifactId: artifactId,
          }, '*');
        }
      } else if (data.type === 'artifact-rendered') {
        setPhase('ready');
        if (onLoadRef.current) onLoadRef.current();
      } else if (data.type === 'artifact-error') {
        const errDetail = data.error?.message || 'Unknown error';
        setErrorMsg(errDetail);
        setPhase('error');
        if (onErrorRef.current) onErrorRef.current(new Error(errDetail));
      } else if (data.type === 'resize-request' && iframeRef.current) {
        if (data.height) {
          iframeRef.current.style.height = `${data.height}px`;
        }
      }
    };

    window.addEventListener('message', handleMessage);

    // Safety timeout — if sandbox doesn't report ready in 90s, show iframe anyway
    const timeout = setTimeout(() => {
      setPhase(prev => prev === 'iframe-loading' ? 'ready' : prev);
    }, 90000);

    return () => {
      window.removeEventListener('message', handleMessage);
      clearTimeout(timeout);
    };
  }, [phase, browserCode, artifactId]);

  return (
    <div className="artifact-visualizer-wrapper" style={{ width, height }}>
      {/* Loading spinner — visible during fetch and iframe load */}
      {(phase === 'loading' || phase === 'iframe-loading') && (
        <div className={`artifact-visualizer-loading ${phase === 'iframe-loading' ? 'building' : ''}`}>
          <div className="loading-spinner"></div>
          <p>{phase === 'loading' ? 'Loading...' : 'Building visualization...'}</p>
        </div>
      )}

      {/* Error state — friendly message for students, details for devs */}
      {phase === 'error' && (
        <div className="artifact-visualizer-error">
          <p className="artifact-error-friendly">This visualization couldn't be displayed. Try asking again.</p>
          {errorMsg && (
            <details className="artifact-error-details">
              <summary>Technical details</summary>
              <pre>{errorMsg}</pre>
            </details>
          )}
        </div>
      )}

      {/* Iframe — mounted once sandbox HTML is ready, uses srcDoc for full isolation */}
      {sandboxHtml && (phase === 'iframe-loading' || phase === 'ready') && (
        <div className={`artifact-visualizer-container ${phase === 'ready' ? 'visible' : ''}`}>
          <iframe
            ref={iframeRef}
            srcDoc={sandboxHtml}
            sandbox="allow-scripts"
            title="Interactive Visualization"
            className="artifact-visualizer-iframe"
          />
        </div>
      )}
    </div>
  );
};

ArtifactVisualizer.propTypes = {
  artifactId: PropTypes.string.isRequired,
  width: PropTypes.string,
  height: PropTypes.string,
  onLoad: PropTypes.func,
  onError: PropTypes.func
};

export default ArtifactVisualizer;
